import React, { useState, useRef, useEffect } from 'react';

// Portfolio data
const portfolioItems = [
  {
    id: 'unity-installation',
    title: 'Interactive Art Installation',
    category: 'Physical Computing',
    symbol: '◆',
    color: 'cyan',
    orbit: { radius: 220, angle: 45, speed: 0.3 },
    description: 'A multi-sensory art installation combining Unity, Arduino, and projection mapping. Visitors interact with physical objects that trigger digital responses, creating an immersive experience that blurs the line between digital and physical.',
    tech: ['Unity', 'C#', 'Arduino', 'Projection Mapping', 'Sensors'],
    images: [
      '/portfolio/installation-1.jpg',
      '/portfolio/installation-2.jpg',
      '/portfolio/installation-3.jpg'
    ],
    year: '2024',
    link: 'https://example.com/project'
  },
  {
    id: 'data-viz',
    title: 'Real-time Data Visualization',
    category: 'Data Visualization',
    symbol: '◇',
    color: 'cyan',
    orbit: { radius: 260, angle: 110, speed: 0.4 },
    description: 'Interactive dashboard visualizing live sensor data from environmental monitoring stations. Built with Unity for immersive 3D data exploration, allowing users to navigate through complex datasets in an intuitive spatial interface.',
    tech: ['Unity', 'C#', 'WebGL', 'Data Analysis', 'Real-time'],
    images: [
      '/portfolio/dataviz-1.jpg',
      '/portfolio/dataviz-2.jpg'
    ],
    year: '2024',
    link: 'https://example.com/project'
  },
  {
    id: 'gesture-control',
    title: 'Gesture-Controlled Interface',
    category: 'Interactive Experience',
    symbol: '◈',
    color: 'orange',
    orbit: { radius: 240, angle: 190, speed: 0.35 },
    description: 'Hands-free control system using depth cameras and machine learning. Users control digital content through natural gestures, creating an intuitive and accessible interface for public installations.',
    tech: ['Unity', 'Kinect', 'ML', 'TouchDesigner', 'Computer Vision'],
    images: [
      '/portfolio/gesture-1.jpg',
      '/portfolio/gesture-2.jpg',
      '/portfolio/gesture-3.jpg',
      '/portfolio/gesture-4.jpg'
    ],
    year: '2023',
    link: 'https://example.com/project'
  },
  {
    id: 'museum-exhibit',
    title: 'Museum Interactive Exhibit',
    category: 'Installation',
    symbol: '◊',
    color: 'orange',
    orbit: { radius: 200, angle: 280, speed: 0.25 },
    description: 'Educational exhibit for science museum featuring interactive displays, physical computing elements, and engaging storytelling. Designed to make complex scientific concepts accessible through hands-on interaction.',
    tech: ['Unity', 'Arduino', 'Raspberry Pi', 'C#', 'Physical Computing'],
    images: [
      '/portfolio/museum-1.jpg',
      '/portfolio/museum-2.jpg'
    ],
    year: '2023',
    link: 'https://example.com/project'
  },
  {
    id: 'sonic-installation',
    title: 'Sonic Landscape',
    category: 'Audio-Visual',
    symbol: '●',
    color: 'cyan',
    orbit: { radius: 180, angle: 350, speed: 0.45 },
    description: 'Interactive sound installation where movement generates audio-visual responses. Combines spatial audio, visual projection, and motion tracking to create an immersive sonic environment.',
    tech: ['Unity', 'Pure Data', 'Spatial Audio', 'Motion Tracking'],
    images: [
      '/portfolio/sonic-1.jpg',
      '/portfolio/sonic-2.jpg',
      '/portfolio/sonic-3.jpg'
    ],
    year: '2024',
    link: 'https://example.com/project'
  }
];

export default function AbstractPortfolio() {
  const [selectedItem, setSelectedItem] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const rotationRef = useRef(0);
  
  // Gallery navigation
  const nextImage = () => {
    if (selectedItem && selectedItem.images) {
      setCurrentImageIndex((prev) => 
        prev === selectedItem.images.length - 1 ? 0 : prev + 1
      );
    }
  };
  
  const prevImage = () => {
    if (selectedItem && selectedItem.images) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? selectedItem.images.length - 1 : prev - 1
      );
    }
  };

  // Animate orbiting spheres
  useEffect(() => {
    const animate = () => {
      rotationRef.current += 0.2;
      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, []);

  // Track mouse for subtle parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
        const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
        setMousePos({ x, y });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleItemClick = (item) => {
    if (selectedItem?.id === item.id) {
      setSelectedItem(null);
      setCurrentImageIndex(0);
    } else {
      setSelectedItem(item);
      setCurrentImageIndex(0); // Reset to first image
      setTimeout(() => {
        // Scroll to the details section
        const portfolioSection = document.getElementById('portfolio');
        if (portfolioSection) {
          window.scrollTo({ 
            top: portfolioSection.offsetTop + 100,
            behavior: 'smooth'
          });
        }
      }, 300);
    }
  };

  // Calculate orbital position
  const getOrbitalPosition = (orbit, time = 0) => {
    const angle = (orbit.angle + time * orbit.speed) * (Math.PI / 180);
    const x = Math.cos(angle) * orbit.radius;
    const y = Math.sin(angle) * orbit.radius;
    return { x, y };
  };

  return (
    <section 
      id="portfolio" 
      className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900 relative overflow-hidden"
      ref={containerRef}
    >
      
      {/* Background layers */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `
          radial-gradient(circle at 30% 40%, rgba(0, 245, 255, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 70% 60%, rgba(255, 107, 53, 0.15) 0%, transparent 50%)`,
      }} />

      {/* Floating grid */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0, 245, 255, 0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 245, 255, 0.5) 1px, transparent 1px)`,
          backgroundSize: '80px 80px',
          transform: `translate(${mousePos.x * 10}px, ${mousePos.y * 10}px)`
        }}
      />

      {/* Header */}
      <div className="relative z-10 text-center pt-24 pb-16">
        <h2 className="text-6xl md:text-7xl font-bold text-white mb-6">Work</h2>
        <div className="h-px w-20 mx-auto bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-40 mb-6" />
        <p className="text-slate-500 text-sm font-mono">
          Navigate the constellation • Click to explore
        </p>
      </div>

      {/* Orbital Portfolio Field - fades out and collapses when project selected */}
      <div 
        className={`relative w-full max-w-7xl mx-auto px-4 transition-all duration-700 ${
          selectedItem ? 'h-0 opacity-0 pointer-events-none' : 'h-[700px] opacity-100'
        }`}
      >
        
        {/* Central core */}
        <div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-0"
          style={{
            transform: `translate(-50%, -50%) translate(${mousePos.x * -5}px, ${mousePos.y * -5}px)`
          }}
        >
          {/* Outer ring */}
          <div className="w-96 h-96 rounded-full border border-cyan-500/10 absolute" />
          <div className="w-80 h-80 rounded-full border border-orange-500/10 absolute top-8 left-8" />
          
          {/* Center glow */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-cyan-500/5 to-orange-500/5 backdrop-blur-sm border border-cyan-500/20" />
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-500/10 to-orange-500/10 blur-xl" />
          </div>
        </div>

        {/* Orbital paths */}
        {portfolioItems.map((item) => (
          <svg 
            key={`path-${item.id}`}
            className="absolute top-1/2 left-1/2 pointer-events-none"
            style={{
              width: `${item.orbit.radius * 2}px`,
              height: `${item.orbit.radius * 2}px`,
              transform: 'translate(-50%, -50%)'
            }}
          >
            <circle
              cx="50%"
              cy="50%"
              r={item.orbit.radius}
              fill="none"
              className={item.color === 'cyan' ? 'stroke-cyan-500' : 'stroke-orange-500'}
              strokeWidth="1"
              opacity="0.05"
              strokeDasharray="4 8"
            />
          </svg>
        ))}

        {/* Portfolio Items as Orbiting Spheres */}
        {portfolioItems.map((item) => {
          const isHovered = hoveredItem?.id === item.id;
          const isSelected = selectedItem?.id === item.id;
          const isActive = isHovered || isSelected;
          
          const pos = getOrbitalPosition(item.orbit, rotationRef.current);
          
          return (
            <div
              key={item.id}
              className="absolute top-1/2 left-1/2 cursor-pointer group"
              style={{
                transform: `translate(-50%, -50%) translate(${pos.x + mousePos.x * 15}px, ${pos.y + mousePos.y * 15}px)`,
                zIndex: isActive ? 100 : 10,
                transition: 'z-index 0s'
              }}
              onClick={() => handleItemClick(item)}
              onMouseEnter={() => setHoveredItem(item)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {/* Connection to center when active */}
              {isActive && (
                <svg 
                  className="absolute top-1/2 left-1/2 pointer-events-none"
                  style={{
                    width: '800px',
                    height: '800px',
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <line
                    x1="400"
                    y1="400"
                    x2={400 - pos.x}
                    y2={400 - pos.y}
                    className={item.color === 'cyan' ? 'stroke-cyan-500' : 'stroke-orange-500'}
                    strokeWidth="1"
                    opacity="0.3"
                  />
                </svg>
              )}

              {/* Ripple effect */}
              {isActive && (
                <>
                  <div 
                    className={`absolute inset-0 rounded-full border-2 animate-ping ${
                      item.color === 'cyan' ? 'border-cyan-500' : 'border-orange-500'
                    }`}
                    style={{
                      width: '180px',
                      height: '180px',
                      marginLeft: '-65px',
                      marginTop: '-65px',
                      animationDuration: '2s'
                    }}
                  />
                  <div 
                    className={`absolute inset-0 rounded-full border animate-ping ${
                      item.color === 'cyan' ? 'border-cyan-500' : 'border-orange-500'
                    }`}
                    style={{
                      width: '140px',
                      height: '140px',
                      marginLeft: '-45px',
                      marginTop: '-45px',
                      animationDuration: '1.5s',
                      animationDelay: '0.5s'
                    }}
                  />
                </>
              )}

              {/* Outer glow */}
              <div 
                className={`absolute rounded-full blur-2xl transition-all duration-700 ${
                  item.color === 'cyan' ? 'bg-cyan-500' : 'bg-orange-500'
                }`}
                style={{
                  width: isActive ? '160px' : '60px',
                  height: isActive ? '160px' : '60px',
                  opacity: isActive ? 0.4 : 0.15,
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  position: 'absolute'
                }}
              />

              {/* Main sphere */}
              <div 
                className={`relative rounded-full backdrop-blur-md transition-all duration-500 flex items-center justify-center border ${
                  item.color === 'cyan' 
                    ? 'bg-cyan-500/5 border-cyan-500' 
                    : 'bg-orange-500/5 border-orange-500'
                } ${isSelected ? 'ring-2 ring-offset-4 ring-offset-slate-950' : ''}`}
                style={{
                  width: isActive ? '100px' : '50px',
                  height: isActive ? '100px' : '50px',
                  borderWidth: isActive ? '2px' : '1px',
                  boxShadow: isActive 
                    ? item.color === 'cyan'
                      ? '0 0 40px rgba(0, 245, 255, 0.5), inset 0 0 20px rgba(0, 245, 255, 0.1)'
                      : '0 0 40px rgba(255, 107, 53, 0.5), inset 0 0 20px rgba(255, 107, 53, 0.1)'
                    : 'none'
                }}
              >
                {/* Symbol */}
                <span 
                  className={`transition-all duration-300 ${
                    item.color === 'cyan' ? 'text-cyan-400' : 'text-orange-400'
                  }`}
                  style={{
                    fontSize: isActive ? '32px' : '20px',
                    opacity: isActive ? 1 : 0.7
                  }}
                >
                  {item.symbol}
                </span>

                {/* Inner particles */}
                {isActive && (
                  <>
                    <div className="absolute w-1 h-1 bg-white rounded-full animate-ping" style={{ top: '30%', left: '70%', animationDuration: '1.5s' }} />
                    <div className="absolute w-1 h-1 bg-white rounded-full animate-ping" style={{ bottom: '30%', right: '70%', animationDuration: '2s', animationDelay: '0.5s' }} />
                  </>
                )}
              </div>

              {/* Info panel on hover */}
              <div 
                className={`absolute left-full ml-8 top-1/2 transform -translate-y-1/2 transition-all duration-500 pointer-events-none ${
                  item.color === 'cyan' ? 'text-cyan-400' : 'text-orange-400'
                }`}
                style={{
                  opacity: isActive ? 1 : 0,
                  transform: isActive 
                    ? 'translate(0, -50%)' 
                    : 'translate(-20px, -50%)',
                  minWidth: '200px'
                }}
              >
                <div className="bg-slate-900/90 backdrop-blur-lg border border-slate-700/50 rounded-lg p-4">
                  <div className={`text-xs font-mono mb-1 ${
                    item.color === 'cyan' ? 'text-cyan-400/70' : 'text-orange-400/70'
                  }`}>
                    {item.category}
                  </div>
                  <div className="text-base font-semibold text-white mb-2">
                    {item.title}
                  </div>
                  <div className="text-xs text-slate-400">
                    {item.year} • Click to explore
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Navigation hint */}
        {!selectedItem && !hoveredItem && (
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-slate-700 text-xs font-mono animate-pulse">
            Spheres orbit naturally • Hover and click to explore
          </div>
        )}
      </div>

      {/* Portfolio Details Panel */}
      <div 
        id="portfolio-details"
        className={`max-w-6xl mx-auto px-6 pt-20 pb-32 transition-all duration-700 ${
          selectedItem ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          maxHeight: selectedItem ? '2000px' : '0',
          overflow: 'hidden'
        }}
      >
        {selectedItem && (
          <div className="relative">
            {/* Back to Projects Button */}
            <div className="mb-8">
              <button
                onClick={() => {
                  setSelectedItem(null);
                  setCurrentImageIndex(0);
                  // Scroll back to top
                  setTimeout(() => {
                    window.scrollTo({ top: document.getElementById('portfolio').offsetTop, behavior: 'smooth' });
                  }, 100);
                }}
                className={`group flex items-center gap-3 px-6 py-3 rounded-full backdrop-blur-md transition-all duration-300 ${
                  selectedItem.color === 'cyan'
                    ? 'bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20 text-cyan-400 hover:text-cyan-300'
                    : 'bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/20 text-orange-400 hover:text-orange-300'
                }`}
              >
                <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="font-mono text-sm">Back to Projects</span>
              </button>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 flex items-center gap-3">
              <div className={`h-px w-20 ${
                selectedItem.color === 'cyan' ? 'bg-cyan-500/30' : 'bg-orange-500/30'
              }`} />
              <span className={`text-2xl ${
                selectedItem.color === 'cyan' ? 'text-cyan-500/50' : 'text-orange-500/50'
              }`}>
                {selectedItem.symbol}
              </span>
              <div className={`h-px w-20 ${
                selectedItem.color === 'cyan' ? 'bg-cyan-500/30' : 'bg-orange-500/30'
              }`} />
            </div>

            <div className="bg-slate-900/30 backdrop-blur-xl rounded-3xl border border-slate-800/50 overflow-hidden">
              
              {/* Header */}
              <div className={`p-10 border-b border-slate-800/50 relative overflow-hidden`}>
                {/* Background gradient */}
                <div className={`absolute inset-0 opacity-5 ${
                  selectedItem.color === 'cyan' 
                    ? 'bg-gradient-to-br from-cyan-500 to-transparent' 
                    : 'bg-gradient-to-br from-orange-500 to-transparent'
                }`} />
                
                <div className="relative flex items-start justify-between">
                  <div>
                    <div className={`text-xs font-mono mb-3 uppercase tracking-widest ${
                      selectedItem.color === 'cyan' ? 'text-cyan-400/70' : 'text-orange-400/70'
                    }`}>
                      {selectedItem.category} • {selectedItem.year}
                    </div>
                    <h3 className="text-4xl md:text-5xl font-bold text-white mb-2">
                      {selectedItem.title}
                    </h3>
                  </div>
                  
                  <button
                    onClick={() => {
                      setSelectedItem(null);
                      setCurrentImageIndex(0);
                      // Scroll back to top
                      setTimeout(() => {
                        window.scrollTo({ top: document.getElementById('portfolio').offsetTop, behavior: 'smooth' });
                      }, 100);
                    }}
                    className={`group flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      selectedItem.color === 'cyan'
                        ? 'text-cyan-400/70 hover:text-cyan-400 hover:bg-cyan-500/10 border border-cyan-500/20'
                        : 'text-orange-400/70 hover:text-orange-400 hover:bg-orange-500/10 border border-orange-500/20'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-sm font-mono">Close</span>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-10">
                
                {/* Image Gallery */}
                <div className={`mb-10 rounded-2xl overflow-hidden border ${
                  selectedItem.color === 'cyan' ? 'border-cyan-500/10' : 'border-orange-500/10'
                }`}>
                  <div className="aspect-video bg-slate-950/50 relative group">
                    {/* Current Image */}
                    {selectedItem.images && selectedItem.images[currentImageIndex] ? (
                      <img 
                        src={selectedItem.images[currentImageIndex]} 
                        alt={`${selectedItem.title} - Image ${currentImageIndex + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback if image doesn't exist
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    
                    {/* Fallback placeholder */}
                    <div className="absolute inset-0 flex items-center justify-center" style={{ display: 'none' }}>
                      <div className={`absolute inset-0 opacity-10 ${
                        selectedItem.color === 'cyan' 
                          ? 'bg-gradient-to-br from-cyan-500 via-transparent to-cyan-500' 
                          : 'bg-gradient-to-br from-orange-500 via-transparent to-orange-500'
                      }`} 
                      style={{
                        animation: 'pulse 4s ease-in-out infinite'
                      }}
                      />
                      
                      <div className="relative text-center">
                        <div className={`text-8xl mb-6 ${
                          selectedItem.color === 'cyan' ? 'text-cyan-500/20' : 'text-orange-500/20'
                        }`}>
                          {selectedItem.symbol}
                        </div>
                        <p className="text-slate-700 text-sm font-mono">
                          Add images to /public{selectedItem.images[currentImageIndex]}
                        </p>
                      </div>
                    </div>
                    
                    {/* Navigation Arrows - Only show if multiple images */}
                    {selectedItem.images && selectedItem.images.length > 1 && (
                      <>
                        {/* Previous Button */}
                        <button
                          onClick={prevImage}
                          className={`absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full backdrop-blur-md transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100 ${
                            selectedItem.color === 'cyan'
                              ? 'bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20 text-cyan-400'
                              : 'bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/20 text-orange-400'
                          }`}
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        
                        {/* Next Button */}
                        <button
                          onClick={nextImage}
                          className={`absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full backdrop-blur-md transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100 ${
                            selectedItem.color === 'cyan'
                              ? 'bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20 text-cyan-400'
                              : 'bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/20 text-orange-400'
                          }`}
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        
                        {/* Image Counter */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full backdrop-blur-md bg-slate-950/50 border border-slate-700/50">
                          <span className="text-sm font-mono text-slate-400">
                            {currentImageIndex + 1} / {selectedItem.images.length}
                          </span>
                        </div>
                        
                        {/* Dot Indicators */}
                        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2">
                          {selectedItem.images.map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentImageIndex(index)}
                              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                index === currentImageIndex
                                  ? selectedItem.color === 'cyan'
                                    ? 'bg-cyan-400 w-6'
                                    : 'bg-orange-400 w-6'
                                  : 'bg-slate-600 hover:bg-slate-500'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="mb-10">
                  <h4 className="text-xl font-semibold text-white mb-4 flex items-center gap-3">
                    <span className={selectedItem.color === 'cyan' ? 'text-cyan-500' : 'text-orange-500'}>
                      ◆
                    </span>
                    About This Project
                  </h4>
                  <p className="text-slate-300 leading-relaxed text-lg">
                    {selectedItem.description}
                  </p>
                </div>

                {/* Technologies */}
                <div>
                  <h4 className="text-xl font-semibold text-white mb-4 flex items-center gap-3">
                    <span className={selectedItem.color === 'cyan' ? 'text-cyan-500' : 'text-orange-500'}>
                      ◇
                    </span>
                    Technologies & Tools
                  </h4>
                  <div className="flex flex-wrap gap-3">
                    {selectedItem.tech.map((tech, index) => (
                      <span
                        key={tech}
                        className={`px-5 py-2 rounded-full text-sm font-medium backdrop-blur-sm border ${
                          selectedItem.color === 'cyan'
                            ? 'bg-cyan-500/5 border-cyan-500/20 text-cyan-400'
                            : 'bg-orange-500/5 border-orange-500/20 text-orange-400'
                        }`}
                        style={{
                          animation: `fadeIn 0.3s ease-out ${index * 0.05}s both`
                        }}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Ambient particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: i % 2 === 0 ? 'rgba(0, 245, 255, 0.1)' : 'rgba(255, 107, 53, 0.1)',
              animation: `float ${10 + Math.random() * 25}s infinite ease-in-out`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translate(0, 0);
            opacity: 0;
          }
          50% {
            transform: translate(${Math.random() * 40 - 20}px, -${Math.random() * 60 + 30}px);
            opacity: 0.5;
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}
